import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useDrawingArea, useXScale, useYScale } from "@mui/x-charts/hooks";

import { ds } from "../../theme/ds";
import { chartPalette } from "../../theme/tokens";
import type { WidgetProps } from "../types";

import { Card, SourceLine } from "./card";
import { makeItemTooltip, tooltipPopperProps } from "./ChartTooltip";
import { formatCurrency0, formatGeneratedAt, formatInt } from "./format";

// Single-series bar chart on the design system's chart spec: title 16px bold
// navy, subtitle 11px gray, one series in CHART_COLORS[1] (Blue), unit on the
// numeric axis, dashed gray-100 gridlines on the value axis only, no legend
// for a single series, bars no wider than about 72px when there are few of
// them. A bar flagged `highlight` is marked in its axis label with
// `highlightSuffix` ("(you)" by default), never with a second colour. Labels
// are aggregated by the provisioner; a band scale collapses duplicate labels,
// so two bars with the same label would silently become one.
//
// Opt-in extras for a time series (exclusivity case study, Elijah 2026-10-27):
// axis titles, short figures ("416K"), a trailing average line with a legend
// above the chart, a light-blue band marking a period, and a change marker
// (the first bar's level carried across to the last bar, the last bar's level
// carried back, and an arrow between them labelled with the change). The
// overlays draw on the vertical layout only.
export function BarChartWidget({ widget }: WidgetProps<"bar_chart">) {
    const {
        title,
        caption,
        layout = "horizontal",
        format = "number",
        source,
        highlightSuffix = "(you)",
        xLabel,
        yLabel,
        compact,
        rollingAverage,
        rollingLabel,
        valueLabel,
        shadeFrom,
        shadeLabel,
        showChange,
        groups,
        hideCategoryLabels,
        plain,
        hideGrid,
    } = widget.props;
    const { bars, generatedAt } = widget.data;

    const short = (v: number) =>
        Math.abs(v) >= 1e6
            ? `${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`
            : Math.abs(v) >= 1e3
              ? `${Math.round(v / 1e3)}K`
              : String(Math.round(v));
    const print = (v: number | null) => {
        if (v == null) return "";
        if (format === "percent") return `${v % 1 === 0 ? v : v.toFixed(1)}%`;
        if (compact) return format === "currency" ? `$${short(v)}` : short(v);
        if (format === "currency") return formatCurrency0(v);
        return formatInt(v);
    };
    // On the bar itself a compact figure drops the dollar sign so it fits the column.
    const barText = (v: number | null) => (v == null ? "" : compact && format !== "percent" ? short(v) : print(v));

    const labels = bars.map((b) => (b.highlight ? `${b.label} ${highlightSuffix}` : b.label));
    const horizontal = layout === "horizontal";
    // Few categories on a vertical chart: widen the gap so bars stay slabs of
    // about 72px, not columns the width of the card.
    const categoryGapRatio = horizontal ? 0.35 : bars.length <= 3 ? 0.7 : bars.length <= 5 ? 0.55 : 0.35;
    const bandAxis = {
        scaleType: "band" as const,
        data: labels,
        categoryGapRatio,
        tickLabelStyle: { fontSize: ds.text.xs, fill: ds.gray700 },
        ...(hideCategoryLabels ? { tickLabelInterval: () => false, disableTicks: true } : {}),
        ...(groups && !bars.some((b) => b.parts)
            ? {
                  colorMap: {
                      type: "ordinal" as const,
                      values: labels,
                      colors: bars.map((b) => chartPalette[Math.max(0, groups.findIndex((g) => g.key === b.group))]),
                  },
              }
            : {}),
    };
    const valueAxis = {
        valueFormatter: (v: number) => print(v),
        tickLabelStyle: { fontSize: ds.text.xs, fill: ds.gray500 },
        tickNumber: 5,
    };
    const axisLabelStyle = { fontSize: ds.text.xs, fill: ds.gray700, fontWeight: 600 };
    // On a vertical chart the value-axis title is drawn by AxisTitle at the card's
    // left edge: the library places it a fixed distance from the axis, where it
    // ran over wide tick labels like "$400K".
    const xAxis = { ...(horizontal ? valueAxis : bandAxis), ...(xLabel ? { label: xLabel, labelStyle: axisLabelStyle } : {}) };
    const yAxis = {
        ...(horizontal ? bandAxis : valueAxis),
        ...(yLabel && horizontal ? { label: yLabel, labelStyle: axisLabelStyle } : {}),
    };
    const longestLabel = Math.max(...labels.map((l) => l.length));
    // A white label inside a bar is unreadable once the bar is shorter than the
    // label; when any non-zero bar is under 12% of the longest, the axis and the
    // tooltip carry the values and the in-bar labels are dropped.
    const maxValue = Math.max(...bars.map((b) => b.value));
    const showBarLabels = bars.every((b) => b.value === 0 || b.value / maxValue >= 0.12);
    const height = horizontal ? Math.max(180, 40 + bars.length * 34) : 300;
    const ItemTooltip = makeItemTooltip(labels, print);
    // Value-axis tick labels need room: "$1,000,000" is far wider than "$400K"
    // (the left figures were cropped at 56px).
    const tickWidth = compact || format === "percent" ? 48 : 80;
    // A trailing average gets a legend above the chart instead of a label on the line.
    const legend = !horizontal && rollingAverage && rollingLabel;
    // Bars carrying parts stack one series per group (the colorMap path is for one group per bar).
    const stacked = bars.some((b) => b.parts);

    return (
        <Card
            sx={{
                display: "flex",
                flexDirection: "column",
                ...(plain ? { border: "none", boxShadow: "none", p: 0, bgcolor: "transparent" } : {}),
            }}
        >
            <Typography
                component="div"
                sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, lineHeight: 1.3 }}
            >
                {title}
            </Typography>
            {caption && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: "4px", maxWidth: 760 }}
                >
                    {caption}
                </Typography>
            )}
            {groups && (
                <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2, mt: 1.5, fontSize: ds.text.xs, color: ds.gray700 }}>
                    {groups.map((g, i) => (
                        <Box key={g.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: chartPalette[i] }} />
                            {g.label}
                        </Box>
                    ))}
                </Box>
            )}
            {legend && (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1.5, fontSize: ds.text.xs, color: ds.gray700 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: chartPalette[0] }} />
                        {valueLabel ?? title}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 16, height: 4, borderRadius: "2px", bgcolor: AVERAGE_COLOR }} />
                        {rollingLabel}
                    </Box>
                </Box>
            )}
            <BarChart
                height={height}
                layout={layout}
                xAxis={[xAxis]}
                yAxis={[yAxis]}
                grid={hideGrid ? undefined : horizontal ? { vertical: true } : { horizontal: true }}
                series={
                    stacked && groups
                        ? groups.map((g, i) => ({
                              data: bars.map((b) => b.parts?.[g.key] ?? 0),
                              label: g.label,
                              color: chartPalette[i],
                              stack: "total",
                              valueFormatter: (v: number | null) => print(v),
                          }))
                        : [
                              {
                                  data: bars.map((b) => b.value),
                                  label: title,
                                  color: chartPalette[0],
                                  valueFormatter: (v: number | null) => print(v),
                              },
                          ]
                }
                barLabel={showBarLabels && !stacked ? (item) => barText(item.value ?? null) : undefined}
                margin={{
                    left: horizontal
                        ? Math.min(240, 24 + longestLabel * 6.5) + (yLabel ? 24 : 0)
                        : tickWidth + (yLabel ? 28 : 8),
                    right: 40,
                    top: shadeLabel || showChange ? 32 : 16,
                    bottom: (horizontal ? 24 : 40) + (xLabel ? 24 : 0),
                }}
                tooltip={{ trigger: "item" }}
                slots={{ itemContent: ItemTooltip }}
                slotProps={{ legend: { hidden: true }, popper: tooltipPopperProps }}
                borderRadius={4}
                sx={{
                    "& .MuiBarLabel-root": { fontSize: ds.text.xs, fill: ds.white, fontWeight: 600 },
                    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: ds.gray200 },
                    "& .MuiChartsGrid-line": { stroke: ds.gray100, strokeDasharray: "4 4" },
                }}
            >
                {!horizontal && (
                    <TimeOverlays
                        labels={labels}
                        values={bars.map((b) => b.value)}
                        yLabel={yLabel}
                        shadeFrom={shadeFrom}
                        shadeLabel={shadeLabel}
                        rollingAverage={rollingAverage}
                        showChange={showChange}
                    />
                )}
            </BarChart>
            <SourceLine sx={{ pt: "8px" }}>
                {source} · {formatGeneratedAt(generatedAt)}
            </SourceLine>
        </Card>
    );
}

// The trailing-average line: design-system Red, 30% heavier than a plain line (Elijah, 2026-10-27).
const AVERAGE_COLOR = chartPalette[4];

type BandScale = { (v: string): number | undefined; bandwidth(): number; step(): number };

function TimeOverlays({
    labels,
    values,
    yLabel,
    shadeFrom,
    shadeLabel,
    rollingAverage,
    showChange,
}: {
    labels: string[];
    values: number[];
    yLabel?: string;
    shadeFrom?: string;
    shadeLabel?: string;
    rollingAverage?: number;
    showChange?: boolean;
}) {
    const x = useXScale() as unknown as BandScale;
    const y = useYScale() as unknown as (v: number) => number;
    const { left, top, width, height } = useDrawingArea();
    if (typeof x.bandwidth !== "function") return null;
    const center = (i: number) => (x(labels[i]) ?? 0) + x.bandwidth() / 2;
    const textStyle = { fontSize: 12, fontWeight: 600 } as const;

    // Shaded period: from the gap before the first matching bar to the right edge,
    // in light blue so it reads against the gray gridlines (15%, Elijah 2026-10-27).
    const shadeAt = shadeFrom ? labels.findIndex((l) => l.startsWith(shadeFrom)) : -1;
    const shadeX = shadeAt >= 0 ? (x(labels[shadeAt]) ?? 0) - (x.step() - x.bandwidth()) / 2 : 0;

    // Trailing average over the last N bars, starting once N bars exist.
    const avg: (number | null)[] =
        rollingAverage && values.length >= rollingAverage
            ? values.map((_, i) =>
                  i + 1 < rollingAverage
                      ? null
                      : values.slice(i + 1 - rollingAverage, i + 1).reduce((a, b) => a + b, 0) / rollingAverage,
              )
            : [];
    const points = avg.flatMap((v, i) => (v == null ? [] : [`${center(i)},${y(v)}`]));

    // Change marker: the first bar's level runs right to the last bar, the last
    // bar's level runs back 100px, and an arrow between the two carries the change.
    const n = values.length - 1;
    const change = showChange && n > 0 && values[0] ? (values[n] - values[0]) / values[0] : null;
    const firstRight = (x(labels[0]) ?? 0) + x.bandwidth();
    const lastLeft = x(labels[n]) ?? 0;
    const arrowX = lastLeft - 50;
    const y0 = y(values[0]);
    const yN = y(values[n]);

    return (
        <g>
            {yLabel && (
                <text
                    transform={`translate(12, ${top + height / 2}) rotate(-90)`}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={ds.gray700}
                    style={{ fontSize: ds.text.xs, fontWeight: 600 }}
                >
                    {yLabel}
                </text>
            )}
            {shadeAt >= 0 && (
                <g>
                    <rect
                        x={shadeX}
                        y={top}
                        width={left + width - shadeX}
                        height={height}
                        fill={chartPalette[8]}
                        fillOpacity={0.15}
                    />
                    {shadeLabel && (
                        <text x={shadeX + 6} y={top - 10} fill={ds.navy} style={textStyle}>
                            {shadeLabel}
                        </text>
                    )}
                </g>
            )}
            {points.length > 1 && (
                <path d={`M ${points.join(" L ")}`} fill="none" stroke={AVERAGE_COLOR} strokeWidth={3.25} />
            )}
            {change != null && (
                <g stroke={chartPalette[1]} strokeWidth={1.5} fill="none">
                    <line x1={firstRight} x2={lastLeft} y1={y0} y2={y0} strokeDasharray="5 4" />
                    <line x1={lastLeft - 100} x2={lastLeft} y1={yN} y2={yN} strokeDasharray="5 4" />
                    <line x1={arrowX} x2={arrowX} y1={y0} y2={yN + 6} strokeWidth={1.95} />
                    <path d={`M ${arrowX - 5} ${yN + 8} L ${arrowX} ${yN + 1} L ${arrowX + 5} ${yN + 8}`} strokeWidth={1.95} />
                    <text
                        x={arrowX - 8}
                        y={(y0 + yN) / 2}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fill={chartPalette[1]}
                        stroke="none"
                        style={{ ...textStyle, fontSize: 14, fontWeight: 800 }}
                    >
                        {`${change >= 0 ? "+" : ""}${Math.round(change * 100)}%`}
                    </text>
                </g>
            )}
        </g>
    );
}
